import React, { useState, useEffect, useMemo } from 'react';
import Page from '../UI/Page';
import { 
  Box, 
  Heading, 
  Table, 
  Thead,
  Tbody,
  Tfoot,
  Tr,
  Th,
  Td,
  Tooltip, 
  Spinner, 
  Center, 
  Text,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  VStack,
  Card,
  CardBody,
  Progress,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  HStack,
  useBreakpointValue,
} from "@chakra-ui/react";
import proxy from '../../proxy';
import axios from "axios";
import Stock from './Stock';
import DividendRow from './DividendRow';
import DividendCalendar from './DividendCalendar';
import DividendMatrix from './DividendMatrix';
import TopIncomeContributors from './TopIncomeContributors';
import useStockPrices from '../../hooks/useStockPrices'; // <-- ADAUGAT

const Stocks = () => {
  const [stocks, setStocks] = useState([]);
  const [dividends, setDividends] = useState([]);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // UN SINGUR fetch pt toate preturile, fara API key in frontend
  const { prices, isLoading: pricesLoading } = useStockPrices();
  const isMobile = useBreakpointValue({ base: true, md: false });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const stocksRes = await axios.get(`${proxy}server_mts/GetStocks.php`, {
          headers: { 'Cache-Control': 'no-cache' },
        });

        if (!stocksRes.data || !Array.isArray(stocksRes.data)) {
          throw new Error("Invalid stock data received.");
        }

        setStocks(stocksRes.data);

        try {
          const dividendsRes = await axios.get(`${proxy}server_mts/GetAllDividends.php`, {
            headers: { 'Cache-Control': 'no-cache' },
          });
          setDividends(dividendsRes.data || []);
        } catch (divError) {
          console.warn("Dividends table not available:", divError.message);
          setDividends([]);
        }

      } catch (error) {
        console.error("Error fetching stocks:", error.message);
        setError("Unable to retrieve the stock data.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  // Calculam sumele folosind preturile din hook (nu mai e nevoie de currentPrices state)
  const { totalDividends, totalPaidDividends, totalValue, totalInvested, totalCommissions } = useMemo(() => {
    const totalDividends = stocks.reduce((sum, stock) => {
      const yearly = stock.div_share * stock.div_per_year * stock.nr_shares;
      return sum + yearly;
    }, 0);

    const totalPaidDividends = stocks.reduce((sum, stock) => {
      return sum + Number(stock.paid_div || 0);
    }, 0);

    const totalInvested = stocks.reduce((sum, stock) => {
      return sum + (stock.average_price_share * stock.nr_shares);
    }, 0);

    const totalCommissions = stocks.reduce((sum, stock) => {
      return sum + Number(stock.commission || 0);
    }, 0);

    // Foloseste preturile din hook
    const totalValue = stocks.reduce((sum, stock) => {
      const currentPrice = prices[stock.ticker] || stock.average_price_share;
      return sum + (currentPrice * stock.nr_shares);
    }, 0);

    const totalProfit = totalValue - totalInvested;
    const avgDividendYield = totalInvested > 0 ? ((totalDividends / totalInvested) * 100).toFixed(2) : 0;

    return { 
      totalDividends, 
      totalPaidDividends, 
      totalValue, 
      totalInvested, 
      totalProfit,
      totalCommissions,
      avgDividendYield
    };
  }, [stocks, prices]); // <-- depinde de prices din hook, nu de currentPrices

  const totalDividendsReceived = dividends.reduce((sum, d) => sum + Number(d.amount || 0), 0);
  const totalTaxPaid = dividends.reduce((sum, d) => sum + Number(d.tax || 0), 0);
  const netDividends = totalDividendsReceived - totalTaxPaid;

  const bestStock = stocks.reduce((best, stock) => {
    const profit = (stock.current_price - stock.average_price_share) / stock.average_price_share * 100;
    const bestProfit = (best.current_price - best.average_price_share) / best.average_price_share * 100;
    return profit > bestProfit ? stock : best;
  }, stocks[0]);

  if (isLoading) {
    return (
      <Page>
        <Center minH="70vh">
          <VStack spacing={4}>
            <Spinner size="xl" color="teal.300" thickness="4px" speed="0.65s" />
            <Text color="gray.400" fontSize="sm">Loading Mattias portfolio...</Text>
          </VStack>
        </Center>
      </Page>
    );
  }

  if (error) {
    return (
      <Page>
        <Center minH="70vh">
          <VStack spacing={4}>
            <Text fontSize="5xl">📉</Text>
            <Text color="red.400" fontSize="lg" fontWeight="semibold">
              {error}
            </Text>
            <Text color="gray.500" fontSize="sm">
              Please refresh the page or check your connection
            </Text>
          </VStack>
        </Center>
      </Page>
    );
  }

  // Header-ul tabelului — definit o singura data, folosit in ambele locuri
  const StockTableHeader = () => (
    <Thead bg="gray.900">
      <Tr>
        <Th color="teal.200" fontSize="xs" textAlign="center" px={2} py={3}>
          <Tooltip label="The name of the company you've invested in" hasArrow>
            Company
          </Tooltip>
        </Th>
        <Th color="teal.200" fontSize="xs" textAlign="center" px={2}>Ticker</Th>
        <Th color="teal.200" fontSize="xs" textAlign="center" px={2}>Sector</Th>
        <Th color="teal.200" fontSize="xs" textAlign="center" px={2}>Shares</Th>
        <Th color="teal.200" fontSize="xs" textAlign="center" px={2}>Price</Th>
        <Th color="teal.200" fontSize="xs" textAlign="center" px={2}>Value</Th>
        <Th color="teal.200" fontSize="xs" textAlign="center" px={2}>Weight</Th>
        <Th color="teal.200" fontSize="xs" textAlign="center" px={2}>Avg/Share</Th>
        <Th color="teal.200" fontSize="xs" textAlign="center" px={2}>Acquisition</Th>
        <Th color="teal.200" fontSize="xs" textAlign="center" px={2}>
          <Tooltip label="Unrealized capital gain/loss from price movement" hasArrow>
            Capital P/L
          </Tooltip>
        </Th>
        <Th color="teal.200" fontSize="xs" textAlign="center" px={2}>
          <Tooltip label="Realized return from dividends received so far" hasArrow>
            Dividend P/L
          </Tooltip>
        </Th>
        <Th color="teal.200" fontSize="xs" textAlign="center" px={2}>
          <Tooltip label="Capital P/L + Dividend P/L" hasArrow>
            Total P/L
          </Tooltip>
        </Th>
        <Th color="teal.200" fontSize="xs" textAlign="center" px={2}>
          <Tooltip label="Yield based on the current market price" hasArrow>
            Yield
          </Tooltip>
        </Th>
        <Th color="teal.200" fontSize="xs" textAlign="center" px={2}>
          <Tooltip label="Yield on Cost — annual dividend vs. your average buy price" hasArrow>
            YoC
          </Tooltip>
        </Th>
        <Th color="teal.200" fontSize="xs" textAlign="center" px={2}>
          <Tooltip label="Consecutive years of dividend increases — cuts and freezes are flagged" hasArrow>
            Streak
          </Tooltip>
        </Th>
        <Th color="teal.200" fontSize="xs" textAlign="center" px={2}>Div/Share</Th>
        <Th color="teal.200" fontSize="xs" textAlign="center" px={2}>Income/Yr</Th>
        <Th color="teal.200" fontSize="xs" textAlign="center" px={2}>Commission</Th>
      </Tr>
    </Thead>
  );

  // Rows-urile — pretul vine din hook, pasam ca prop + weight
  const totalPortfolioValue = stocks.reduce((sum, st) => {
    const p = prices[st.ticker] || 0;
    return sum + p * st.nr_shares;
  }, 0);

  // Helper: calc stock props (weight etc)
  const getStockProps = (st) => {
    const stockValue = (prices[st.ticker] || 0) * st.nr_shares;
    const weight = totalPortfolioValue > 0 ? (stockValue / totalPortfolioValue) * 100 : 0;
    return {
      stock: st,
      key: st.id,
      price: prices[st.ticker] || null,
      isLoading: pricesLoading,
      weight,
    };
  };

  // Desktop: table body
  const StockTableBody = () => (
    <Tbody>
      {stocks.map((st) => (
        <Stock {...getStockProps(st)} key={st.id} isMobile={false} />
      ))}
    </Tbody>
  );

  // Totals for footer / summary card
  const totalCapitalPL = totalValue - totalInvested;
  const totalDividendPL = totalPaidDividends;
  const totalCombinedPL = totalCapitalPL + totalDividendPL;
  const totalCapitalPLPct  = totalInvested > 0 ? (totalCapitalPL  / totalInvested) * 100 : 0;
  const totalDividendPLPct = totalInvested > 0 ? (totalDividendPL / totalInvested) * 100 : 0;
  const totalCombinedPLPct = totalInvested > 0 ? (totalCombinedPL / totalInvested) * 100 : 0;

  // Portfolio-level yields: current (vs. market value) and on cost (vs. invested)
  const portfolioCurrentYield = totalValue    > 0 ? (totalDividends / totalValue)    * 100 : 0;
  const portfolioYoC          = totalInvested > 0 ? (totalDividends / totalInvested) * 100 : 0;

  // Capital recovered through dividends (sheet "% Recuperare", la nivel de portofoliu)
  // Cost de baza = investit; comisionul e deja inclus in average_price_share, nu se mai aduna separat
  const totalCostBasis = totalInvested;
  // Capitalul recuperat = dividende nete (după taxe), la fel ca în cardul "Dividends (After Tax)";
  // fallback la paid_div din sheet când nu există tabelul de dividende
  const recoveredDividends = dividends.length > 0 ? netDividends : totalPaidDividends;
  const recoveryPct    = totalCostBasis > 0 ? (recoveredDividends / totalCostBasis) * 100 : 0;

  const fmtMoney = (n) =>
    `${n < 0 ? "−" : ""}$${Math.abs(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const fmtPct = (n) => `${n >= 0 ? "+" : "−"}${Math.abs(n).toFixed(2)}%`;
  const plColor = (n) => (n > 0 ? "green.300" : n < 0 ? "red.300" : "gray.300");

  // Desktop: footer with totals
  const StockTableFooter = () => (
    <Tfoot bg="gray.900">
      <Tr>
        <Td textAlign="center" fontWeight="bold" color="teal.200" fontSize="xs">TOTAL</Td>
        <Td />
        <Td />
        <Td />
        <Td />
        <Td textAlign="center" fontWeight="bold" color="blue.300" fontSize="sm">
          {fmtMoney(totalValue)}
        </Td>
        <Td textAlign="center" fontWeight="bold" color="teal.300" fontSize="xs">100.00%</Td>
        <Td />
        <Td textAlign="center" fontWeight="bold" color="gray.300" fontSize="sm">
          {fmtMoney(totalInvested)}
        </Td>
        <Td textAlign="center">
          <VStack spacing={0}>
            <Text fontSize="sm" fontWeight="bold" color={plColor(totalCapitalPL)}>
              {fmtMoney(totalCapitalPL)}
            </Text>
            <Text fontSize="2xs" color={plColor(totalCapitalPL)}>
              {fmtPct(totalCapitalPLPct)}
            </Text>
          </VStack>
        </Td>
        <Td textAlign="center">
          <VStack spacing={0}>
            <Text fontSize="sm" fontWeight="bold" color="yellow.300">
              {fmtMoney(totalDividendPL)}
            </Text>
            <Text fontSize="2xs" color="yellow.500">
              {fmtPct(totalDividendPLPct)}
            </Text>
          </VStack>
        </Td>
        <Td textAlign="center">
          <VStack spacing={0}>
            <Text fontSize="sm" fontWeight="black" color={plColor(totalCombinedPL)}>
              {fmtMoney(totalCombinedPL)}
            </Text>
            <Text fontSize="2xs" color={plColor(totalCombinedPL)}>
              {fmtPct(totalCombinedPLPct)}
            </Text>
          </VStack>
        </Td>
        <Td textAlign="center" fontSize="xs" color="teal.300" fontWeight="bold">
          {portfolioCurrentYield.toFixed(2)}%
        </Td>
        <Td textAlign="center" fontSize="xs" color="teal.300" fontWeight="bold">
          {portfolioYoC.toFixed(2)}%
        </Td>
        <Td />{/* Streak */}
        <Td />
        <Td textAlign="center" fontWeight="bold" color="green.300" fontSize="sm">
          {fmtMoney(totalDividends)}
        </Td>
        <Td textAlign="center" fontSize="sm" color="gray.400">
          {fmtMoney(totalCommissions)}
        </Td>
      </Tr>
    </Tfoot>
  );

  // Mobile: card list + totals summary card
  const MobileTotalsCard = () => (
    <Box
      p={3}
      bg="gray.900"
      borderRadius="xl"
      border="1px solid"
      borderColor="teal.700"
      mt={1}
    >
      <Text fontSize="xs" color="teal.300" fontWeight="bold" mb={2} textAlign="center">
        PORTFOLIO TOTALS
      </Text>
      <SimpleGrid columns={3} spacing={2} mb={2}>
        <VStack spacing={0}>
          <Text fontSize="2xs" color="gray.500">Capital P/L</Text>
          <Text fontSize="sm" fontWeight="bold" color={plColor(totalCapitalPL)}>
            {fmtMoney(totalCapitalPL)}
          </Text>
          <Text fontSize="2xs" color={plColor(totalCapitalPL)}>
            {fmtPct(totalCapitalPLPct)}
          </Text>
        </VStack>
        <VStack spacing={0}>
          <Text fontSize="2xs" color="gray.500">Dividend P/L</Text>
          <Text fontSize="sm" fontWeight="bold" color="yellow.300">
            {fmtMoney(totalDividendPL)}
          </Text>
          <Text fontSize="2xs" color="yellow.500">
            {fmtPct(totalDividendPLPct)}
          </Text>
        </VStack>
        <VStack spacing={0}>
          <Text fontSize="2xs" color="gray.500">Total P/L</Text>
          <Text fontSize="sm" fontWeight="black" color={plColor(totalCombinedPL)}>
            {fmtMoney(totalCombinedPL)}
          </Text>
          <Text fontSize="2xs" color={plColor(totalCombinedPL)}>
            {fmtPct(totalCombinedPLPct)}
          </Text>
        </VStack>
      </SimpleGrid>
      <SimpleGrid columns={2} spacing={2} pt={2} borderTop="1px solid" borderColor="whiteAlpha.100">
        <VStack spacing={0}>
          <Text fontSize="2xs" color="gray.500">Value / Invested</Text>
          <Text fontSize="xs" color="blue.300">
            {fmtMoney(totalValue)} / {fmtMoney(totalInvested)}
          </Text>
        </VStack>
        <VStack spacing={0}>
          <Text fontSize="2xs" color="gray.500">Income/Yr · Commissions</Text>
          <Text fontSize="xs" color="green.300">
            {fmtMoney(totalDividends)} · {fmtMoney(totalCommissions)}
          </Text>
        </VStack>
      </SimpleGrid>
    </Box>
  );

  // Mobile: card list
  const StockCards = () => (
    <VStack spacing={2} align="stretch">
      {stocks.map((st) => (
        <Stock {...getStockProps(st)} key={st.id} isMobile={true} />
      ))}
      <MobileTotalsCard />
    </VStack>
  );

  // Stocks content — switches between table and cards
  const StocksContent = () => {
    if (isMobile) {
      return (
        <Box
          p={3}
          borderWidth="1px"
          borderColor="gray.700"
          borderRadius="lg"
          boxShadow="lg"
          width="100%"
          bg="gray.800"
        >
          <StockCards />
        </Box>
      );
    }

    return (
      <Box
        p={4}
        borderWidth="1px"
        borderColor="gray.700"
        borderRadius="lg"
        boxShadow="lg"
        width="100%"
        overflowX="auto"
        bg="gray.800"
      >
        <Table variant="simple" size="sm" colorScheme="teal">
          <StockTableHeader />
          <StockTableBody />
          <StockTableFooter />
        </Table>
      </Box>
    );
  };

  return (
    <Page>
      <div className="hero" style={{ minHeight: '70vh' }}>
        <VStack spacing={6} p={{ base: 2, md: 4 }} mt={6}>
          
          {/* Header Section */}
          <VStack spacing={1} mb={4}>
            <Heading size="sm" color="teal.300" fontWeight="semibold">
              Mattias Assets Portfolio
            </Heading>
            <Text fontSize="xs" color="gray.500">📈 Investment Tracker</Text>
          </VStack>

          {/* Summary Statistics Cards */}
          <SimpleGrid 
            columns={{ base: 2, md: 4, lg: 4 }} 
            spacing={4} 
            width="100%"
            mb={4}
          >
            <Card 
              bg="gray.800" 
              borderWidth="1px" 
              borderColor="gray.700"
              boxShadow="lg"
              _hover={{ transform: "translateY(-2px)", boxShadow: "xl" }}
              transition="all 0.2s"
            >
              <CardBody p={4}>
                <Stat>
                  <StatLabel color="gray.400" fontSize="xs">Total Stocks</StatLabel>
                  <StatNumber color="purple.300" fontSize={{ base: "lg", md: "xl" }}>
                    {stocks.length}
                  </StatNumber>
                  <StatHelpText color="gray.500" fontSize="xs">Companies</StatHelpText>
                </Stat>
              </CardBody>
            </Card>

            <Card bg="gray.800" borderWidth="1px" borderColor="gray.700" boxShadow="lg"
              _hover={{ transform: "translateY(-2px)", boxShadow: "xl" }}
              transition="all 0.2s">
              <CardBody p={4}>
                <Stat>
                  <StatLabel color="gray.400" fontSize="xs">Best Performer</StatLabel>
                  <StatNumber color="green.300" fontSize={{ base: "lg", md: "xl" }}>
                    {bestStock?.ticker || 'N/A'}
                  </StatNumber>
                  <StatHelpText color="gray.500" fontSize="xs">Top stock</StatHelpText>
                </Stat>
              </CardBody>
            </Card>

            <Card 
              bg="gray.800" 
              borderWidth="1px" 
              borderColor="gray.700"
              boxShadow="lg"
              _hover={{ transform: "translateY(-2px)", boxShadow: "xl" }}
              transition="all 0.2s"
            >
              <CardBody p={4}>
                <Stat>
                  <StatLabel color="gray.400" fontSize="xs">Income / Year</StatLabel>
                  <StatNumber color="green.300" fontSize={{ base: "lg", md: "xl" }}>
                    ${totalDividends.toFixed(2)}
                  </StatNumber>
                  <StatHelpText color="gray.500" fontSize="xs">Expected annually</StatHelpText>
                </Stat>
              </CardBody>
            </Card>

            <Card 
              bg="gray.800" 
              borderWidth="1px" 
              borderColor="gray.700"
              boxShadow="lg"
              _hover={{ transform: "translateY(-2px)", boxShadow: "xl" }}
              transition="all 0.2s"
            >
              <CardBody p={4}>
                <Stat>
                  <StatLabel color="gray.400" fontSize="xs">
                    {dividends.length > 0 ? "Dividends (After Tax)" : "Paid Dividends"}
                  </StatLabel>
                  <StatNumber color="yellow.300" fontSize={{ base: "lg", md: "xl" }}>
                    ${dividends.length > 0 ? netDividends.toFixed(2) : totalPaidDividends.toFixed(2)}
                  </StatNumber>
                  <StatHelpText color="gray.500" fontSize="xs">Received so far</StatHelpText>
                </Stat>
              </CardBody>
            </Card>
          </SimpleGrid>

          {/* Capital recovered through dividends — "% Recuperare" la nivel de portofoliu */}
          <Box width="100%">
            <Box
              bg="gray.800"
              borderWidth="1px"
              borderColor="teal.700"
              borderRadius="xl"
              boxShadow="lg"
              p={4}
            >
              <HStack justify="space-between" mb={2} flexWrap="wrap" spacing={2}>
                <Text
                  fontSize={{ base: "xs", md: "sm" }}
                  color="teal.300"
                  fontWeight="bold"
                  letterSpacing="wide"
                  textTransform="uppercase"
                >
                  💰 Capital Recovered via Dividends
                </Text>
                <Text fontSize={{ base: "xs", md: "sm" }} color="gray.400">
                  {fmtMoney(recoveredDividends)} of {fmtMoney(totalCostBasis)}
                </Text>
              </HStack>
              <Progress
                value={Math.min(recoveryPct, 100)}
                colorScheme={recoveryPct >= 100 ? "green" : recoveryPct >= 50 ? "teal" : "yellow"}
                bg="gray.700"
                borderRadius="full"
                size="md"
                hasStripe
              />
              <Text fontSize="xs" color="gray.500" mt={1.5}>
                <Text as="span" color="green.300" fontWeight="bold">
                  {recoveryPct.toFixed(2)}%
                </Text>{" "}
                of your invested capital has already returned as dividends
              </Text>
            </Box>
          </Box>

          {/* Top dividend income contributors */}
          <Box width="100%" mb={2}>
            <TopIncomeContributors stocks={stocks} />
          </Box>

          {/* Tabs: Stocks + Calendar always, Dividends only if data exists */}
          <Box width="100%">
            <Tabs variant="soft-rounded" colorScheme="teal">
              <TabList mb={4} justifyContent="center" flexWrap="wrap">
                <Tab fontSize="sm" fontWeight="semibold">
                  📊 Stocks ({stocks.length})
                </Tab>
                <Tab fontSize="sm" fontWeight="semibold">
                  📅 Calendar
                </Tab>
                <Tab fontSize="sm" fontWeight="semibold">
                  📊 History
                </Tab>
                {dividends.length > 0 && (
                  <Tab fontSize="sm" fontWeight="semibold">
                    💰 Dividends ({dividends.length})
                  </Tab>
                )}
              </TabList>

              <TabPanels>
                <TabPanel p={0}>
                  <StocksContent />
                </TabPanel>

                <TabPanel p={0}>
                  <DividendCalendar />
                </TabPanel>

                <TabPanel p={0}>
                  <DividendMatrix />
                </TabPanel>

                {dividends.length > 0 && (
                  <TabPanel p={0}>
                    <Box
                      p={{ base: 3, md: 4 }}
                      borderWidth="1px"
                      borderColor="gray.700"
                      borderRadius="lg"
                      boxShadow="lg"
                      width="100%"
                      overflowX="auto"
                      bg="gray.800"
                    >
                      <HStack justify="space-around" mb={4} flexWrap="wrap" spacing={{ base: 2, md: 4 }}>
                        <VStack spacing={0}>
                          <Text fontSize="xs" color="gray.500">Total Paid</Text>
                          <Text fontSize={{ base: "md", md: "lg" }} fontWeight="bold" color="green.300">
                            ${totalDividendsReceived.toFixed(2)}
                          </Text>
                        </VStack>
                        <VStack spacing={0}>
                          <Text fontSize="xs" color="gray.500">Total Tax</Text>
                          <Text fontSize={{ base: "md", md: "lg" }} fontWeight="bold" color="red.300">
                            ${totalTaxPaid.toFixed(2)}
                          </Text>
                        </VStack>
                        <VStack spacing={0}>
                          <Text fontSize="xs" color="gray.500">Net Amount</Text>
                          <Text fontSize={{ base: "md", md: "lg" }} fontWeight="bold" color="teal.300">
                            ${netDividends.toFixed(2)}
                          </Text>
                        </VStack>
                      </HStack>

                      {isMobile ? (
                        <VStack spacing={2} align="stretch">
                          {dividends.map((dividend) => (
                            <DividendRow key={dividend.id} dividend={dividend} isMobile />
                          ))}
                        </VStack>
                      ) : (
                        <Table variant="simple" size="sm">
                          <Thead bg="gray.900">
                            <Tr>
                              <Th color="green.200" fontSize="xs" textAlign="center" px={2}>Date</Th>
                              <Th color="green.200" fontSize="xs" textAlign="center" px={2}>Ticker</Th>
                              <Th color="green.200" fontSize="xs" textAlign="center" px={2}>Description</Th>
                              <Th color="green.200" fontSize="xs" textAlign="center" px={2}>Amount</Th>
                              <Th color="green.200" fontSize="xs" textAlign="center" px={2}>Tax</Th>
                              <Th color="green.200" fontSize="xs" textAlign="center" px={2}>Net</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {dividends.map((dividend) => (
                              <DividendRow key={dividend.id} dividend={dividend} />
                            ))}
                          </Tbody>
                        </Table>
                      )}
                    </Box>
                  </TabPanel>
                )}
              </TabPanels>
            </Tabs>
          </Box>
        </VStack>
      </div>
    </Page>
  );
};

export default Stocks;